import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, Loader2, Save, Moon, Sun, ArrowUp, Home } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const Astrology = () => {
  const { token } = useAuth();
  const [birthChart, setBirthChart] = useState(null);
  const [fullChart, setFullChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchBirthChart();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBirthChart = async () => {
    try {
      const res = await axios.get(`${API}/astrology/birth-chart`, { headers, withCredentials: true });
      if (res.data && !res.data.message) {
        setBirthChart(res.data);
        setBirthDate(res.data.birth_date || "");
        setBirthTime(res.data.birth_time || "");
        setCity(res.data.city || "");
        setState(res.data.state || "");
        setCountry(res.data.country || "");
        
        if (res.data.full_chart) {
          setFullChart(res.data.full_chart);
        }
      }
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!birthDate || !birthTime || !city || !country) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/astrology/birth-chart`, {
        birth_date: birthDate,
        birth_time: birthTime,
        city: city,
        state: state || null,
        country: country
      }, { headers, withCredentials: true });
      
      toast.success("Birth chart calculated!");
      
      // Fetch full chart
      const fullRes = await axios.get(`${API}/astrology/full-chart`, { headers, withCredentials: true });
      setFullChart(fullRes.data);
      setBirthChart({ ...res.data, full_chart: fullRes.data });
    } catch (error) {
      toast.error("Failed to save birth chart");
    } finally {
      setSaving(false);
    }
  };

  const elementColors = {
    "Fire": "text-orange-400",
    "Earth": "text-green-400",
    "Air": "text-cyan-400",
    "Water": "text-blue-400"
  };

  const elementBgColors = {
    "Fire": "from-orange-500/20",
    "Earth": "from-green-500/20",
    "Air": "from-cyan-500/20",
    "Water": "from-blue-500/20"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="astrology-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Star className="w-8 h-8 text-moon-gold" />
          Birth Chart
        </h1>
        <p className="text-mystic-200/70">Your complete Western astrology natal chart</p>
      </motion.div>

      {/* Birth Information Form */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="glass">
          <CardHeader>
            <CardTitle className="text-lg text-white">Birth Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-mystic-100">Birth Date *</Label>
                <Input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="bg-mystic-900/50 border-white/10 text-white"
                  data-testid="birth-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-mystic-100">Birth Time *</Label>
                <Input
                  type="time"
                  value={birthTime}
                  onChange={(e) => setBirthTime(e.target.value)}
                  className="bg-mystic-900/50 border-white/10 text-white"
                  data-testid="birth-time-input"
                />
                <p className="text-mystic-200/50 text-xs">Exact time needed for Rising sign</p>
              </div>
              <div className="space-y-2">
                <Label className="text-mystic-100">City *</Label>
                <Input
                  type="text"
                  placeholder="e.g., New York"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-mystic-100">State/Province</Label>
                <Input
                  type="text"
                  placeholder="e.g., NY or New York"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                  data-testid="state-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-mystic-100">Country *</Label>
                <Input
                  type="text"
                  placeholder="e.g., USA"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                  data-testid="country-input"
                />
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full md:w-auto bg-mystic-300 hover:bg-mystic-200 text-white rounded-full"
              data-testid="save-birth-chart-btn"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Calculate Chart</>}
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Big Three */}
      {fullChart && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h2 className="text-xl font-semibold text-white mb-4">The Big Three</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Sun Sign */}
            <Card className={`glass bg-gradient-to-br ${elementBgColors[fullChart.sun?.element] || ''} to-transparent`}>
              <CardContent className="py-6 text-center">
                <Sun className="w-10 h-10 text-moon-gold mx-auto mb-3" />
                <p className="text-mystic-300 text-sm mb-1">Sun Sign</p>
                <p className="text-4xl mb-2">{fullChart.sun?.symbol}</p>
                <h3 className="text-2xl font-bold text-white">{fullChart.sun?.sign}</h3>
                <p className="text-mystic-200/70 text-sm">{fullChart.sun?.degree}°</p>
                <div className="mt-3 glass-light rounded-lg p-2">
                  <p className={`text-sm ${elementColors[fullChart.sun?.element]}`}>
                    {fullChart.sun?.element} • {fullChart.sun?.modality}
                  </p>
                </div>
                <p className="text-mystic-200/60 text-xs mt-2">{fullChart.sun?.meaning}</p>
              </CardContent>
            </Card>

            {/* Moon Sign */}
            <Card className={`glass bg-gradient-to-br ${elementBgColors[fullChart.moon?.element] || ''} to-transparent`}>
              <CardContent className="py-6 text-center">
                <Moon className="w-10 h-10 text-moon-silver mx-auto mb-3" />
                <p className="text-mystic-300 text-sm mb-1">Moon Sign</p>
                <p className="text-4xl mb-2">{fullChart.moon?.symbol}</p>
                <h3 className="text-2xl font-bold text-white">{fullChart.moon?.sign}</h3>
                <p className="text-mystic-200/70 text-sm">{fullChart.moon?.degree}°</p>
                <div className="mt-3 glass-light rounded-lg p-2">
                  <p className={`text-sm ${elementColors[fullChart.moon?.element]}`}>
                    {fullChart.moon?.element} • {fullChart.moon?.modality}
                  </p>
                </div>
                <p className="text-mystic-200/60 text-xs mt-2">{fullChart.moon?.meaning}</p>
              </CardContent>
            </Card>

            {/* Rising Sign */}
            <Card className={`glass bg-gradient-to-br ${elementBgColors[fullChart.rising?.element] || ''} to-transparent`}>
              <CardContent className="py-6 text-center">
                <ArrowUp className="w-10 h-10 text-mystic-300 mx-auto mb-3" />
                <p className="text-mystic-300 text-sm mb-1">Rising Sign (Ascendant)</p>
                <p className="text-4xl mb-2">{fullChart.rising?.symbol || "?"}</p>
                <h3 className="text-2xl font-bold text-white">{fullChart.rising?.sign || "Unknown"}</h3>
                <p className="text-mystic-200/70 text-sm">{fullChart.rising?.degree ? `${fullChart.rising.degree}°` : "Need birth time & location"}</p>
                {fullChart.rising?.element && (
                  <div className="mt-3 glass-light rounded-lg p-2">
                    <p className={`text-sm ${elementColors[fullChart.rising?.element]}`}>
                      {fullChart.rising?.element}
                    </p>
                  </div>
                )}
                <p className="text-mystic-200/60 text-xs mt-2">{fullChart.rising?.meaning}</p>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      )}

      {/* Full Chart Details */}
      {fullChart && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Tabs defaultValue="planets" className="w-full">
            <TabsList className="grid grid-cols-3 bg-mystic-800/50 p-1 rounded-xl mb-4">
              <TabsTrigger value="planets" className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg">
                Planets
              </TabsTrigger>
              <TabsTrigger value="houses" className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg">
                Houses
              </TabsTrigger>
              <TabsTrigger value="chart" className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg">
                Chart Wheel
              </TabsTrigger>
            </TabsList>

            {/* Planets Tab */}
            <TabsContent value="planets">
              <Card className="glass">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Planetary Placements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {fullChart.planets && Object.entries(fullChart.planets).map(([planet, data]) => (
                      <div key={planet} className="glass-light rounded-xl p-4 flex items-center gap-4">
                        <div className="text-3xl">{data.symbol}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="text-white font-medium">{planet}</h4>
                            <span className="text-mystic-200/70 text-sm">{data.degree_in_sign?.toFixed(1)}°</span>
                          </div>
                          <p className="text-mystic-300">{data.sign}</p>
                          <p className="text-mystic-200/60 text-xs mt-1">{data.meaning}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Houses Tab */}
            <TabsContent value="houses">
              <Card className="glass">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    The 12 Houses
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {fullChart.houses && fullChart.houses.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {fullChart.houses.map((house) => (
                        <div key={house.house} className="glass-light rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-mystic-300 text-sm">House {house.house}</span>
                            <span className="text-2xl">{house.symbol}</span>
                          </div>
                          <h4 className="text-white font-medium">{house.sign}</h4>
                          <p className="text-mystic-200/70 text-sm">{house.degree_in_sign?.toFixed(1)}°</p>
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <p className="text-mystic-300 text-xs font-medium">{house.theme}</p>
                            <p className="text-mystic-200/60 text-xs mt-1">{house.rules}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-mystic-200/70 text-center py-8">
                      Enter birth time and location to calculate house positions
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Chart Wheel Tab */}
            <TabsContent value="chart">
              <Card className="glass">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Natal Chart Wheel</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative w-full max-w-lg mx-auto aspect-square">
                    {/* Outer circle - signs */}
                    <div className="absolute inset-0 rounded-full border-2 border-mystic-600/50"></div>
                    <div className="absolute inset-[10%] rounded-full border border-mystic-500/30"></div>
                    <div className="absolute inset-[20%] rounded-full border border-mystic-400/20"></div>
                    <div className="absolute inset-[35%] rounded-full border border-mystic-300/10"></div>
                    
                    {/* Zodiac signs around wheel */}
                    {["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"].map((symbol, i) => {
                      const angle = (i * 30 - 90) * (Math.PI / 180);
                      const radius = 46;
                      const x = 50 + radius * Math.cos(angle);
                      const y = 50 + radius * Math.sin(angle);
                      const isSunSign = symbol === fullChart.sun?.symbol;
                      const isMoonSign = symbol === fullChart.moon?.symbol;
                      const isRisingSign = symbol === fullChart.rising?.symbol;
                      
                      return (
                        <div
                          key={`astro-${i}`}
                          className={`absolute text-xl transition-all ${
                            isSunSign ? 'text-moon-gold scale-125' : 
                            isMoonSign ? 'text-moon-silver scale-110' :
                            isRisingSign ? 'text-mystic-300 scale-110' :
                            'text-mystic-500'
                          }`}
                          style={{
                            left: `${x}%`,
                            top: `${y}%`,
                            transform: 'translate(-50%, -50%)'
                          }}
                          title={["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"][i]}
                        >
                          {symbol}
                        </div>
                      );
                    })}
                    
                    {/* Planets */}
                    {fullChart.planets && Object.entries(fullChart.planets).map(([planet, data], i) => {
                      const angle = ((data.longitude || 0) - 90) * (Math.PI / 180);
                      const radius = 28;
                      const x = 50 + radius * Math.cos(angle);
                      const y = 50 + radius * Math.sin(angle);
                      
                      return (
                        <div
                          key={planet}
                          className="absolute text-sm text-white bg-mystic-700/80 rounded-full w-6 h-6 flex items-center justify-center"
                          style={{
                            left: `${x}%`,
                            top: `${y}%`,
                            transform: 'translate(-50%, -50%)'
                          }}
                          title={`${planet} in ${data.sign}`}
                        >
                          {data.symbol}
                        </div>
                      );
                    })}
                    
                    {/* Center info */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center bg-mystic-900/80 rounded-full w-24 h-24 flex flex-col items-center justify-center">
                        <span className="text-4xl">{fullChart.sun?.symbol}</span>
                        <span className="text-mystic-200/70 text-xs">{fullChart.sun?.sign}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Legend */}
                  <div className="flex flex-wrap justify-center gap-4 mt-6">
                    <div className="flex items-center gap-2">
                      <span className="text-moon-gold">●</span>
                      <span className="text-mystic-200/70 text-sm">Sun Sign</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-moon-silver">●</span>
                      <span className="text-mystic-200/70 text-sm">Moon Sign</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-mystic-300">●</span>
                      <span className="text-mystic-200/70 text-sm">Rising Sign</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      )}

      {/* Interpretation */}
      {fullChart && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="glass-heavy">
            <CardContent className="py-8">
              <h3 className="text-xl font-semibold text-white mb-4 text-center">
                Your Cosmic Profile
              </h3>
              <div className="max-w-2xl mx-auto space-y-4 text-mystic-200/80">
                <p>
                  With your <span className="text-moon-gold font-medium">Sun in {fullChart.sun?.sign}</span>, 
                  your core identity is shaped by {fullChart.sun?.element?.toLowerCase()} energy - 
                  {fullChart.sun?.traits?.join(", ").toLowerCase()}.
                </p>
                <p>
                  Your <span className="text-moon-silver font-medium">Moon in {fullChart.moon?.sign}</span> reveals 
                  your emotional nature. You process feelings through a {fullChart.moon?.element?.toLowerCase()} lens, 
                  seeking {fullChart.moon?.element === "Water" ? "emotional depth and intuition" : 
                           fullChart.moon?.element === "Fire" ? "passion and excitement" :
                           fullChart.moon?.element === "Earth" ? "stability and security" :
                           "intellectual understanding and communication"}.
                </p>
                {fullChart.rising?.sign && fullChart.rising?.sign !== "Unknown" && (
                  <p>
                    Your <span className="text-mystic-300 font-medium">{fullChart.rising?.sign} Rising</span> shapes 
                    how others first perceive you. You come across as {
                      fullChart.rising?.element === "Fire" ? "confident, energetic, and dynamic" :
                      fullChart.rising?.element === "Earth" ? "grounded, practical, and reliable" :
                      fullChart.rising?.element === "Air" ? "intellectual, communicative, and friendly" :
                      "intuitive, emotional, and sensitive"
                    }.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default Astrology;
